import {
  COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
  CollapsibleScreenHeader,
} from "@/components/prooftree/CollapsibleScreenHeader"
import { EmptyState } from "@/components/prooftree/EmptyState"
import { PressableScale } from "@/components/prooftree/PressableScale"
import { ProgressRing } from "@/components/prooftree/ProgressRing"
import { Avatar, AvatarFallbackText } from "@/components/ui/avatar"
import { Box } from "@/components/ui/box"
import { Button, ButtonText } from "@/components/ui/button"
import { HStack } from "@/components/ui/hstack"
import { Progress, ProgressFilledTrack } from "@/components/ui/progress"
import { Text } from "@/components/ui/text"
import { VStack } from "@/components/ui/vstack"
import { screenCopy } from "@/constants/Brand"
import { Colors } from "@/constants/Colors"
import { useAuth } from "@/contexts/AuthContext"
import {
  allSkills,
  CertificatePathway,
  certificatePathways,
  CoachRecommendation,
  getCoachRecommendations,
  getSkill,
  getSkillDomainColor,
  mockContacts,
  NetworkContact,
  passportBadges,
} from "@/data/prooftree"
import { createEqContact, listEqContacts } from "@/services/eqParityBackend"
import { getIntegrationStatuses } from "@/services/integrations"
import {
  getNotificationPreferences,
  updateNotificationPreferences,
} from "@/services/notificationPreferences"
import { sharePassportLink } from "@/services/passportShare"
import {
  getEqPassportSummary,
  isSupabaseUuid,
  type EqPassportSummary,
} from "@/services/prooftreeBackend"
import {
  buildPublicPassportUrl,
  getPublicPassportSettings,
  publishEqPassport,
} from "@/services/publicPassport"
import { router } from "expo-router"
import {
  Award,
  Bell,
  BookOpenText,
  ChevronRight,
  Download,
  LogIn,
  LogOut,
  MessageSquareWarning,
  Moon,
  PlayCircle,
  QrCode,
  Share2,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Trophy,
  UserPlus,
  UsersRound,
} from "lucide-react-native"
import { useEffect, useRef, useState } from "react"
import { Animated, Platform, Switch, TextInput, TouchableOpacity, View } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

const stats = [
  { label: "Skills", value: "9" },
  { label: "Levels", value: "16" },
  { label: "Proofs", value: "24" },
  { label: "Streak", value: "7d" },
]

const settings = [
  { label: "Dark theme", icon: Moon, enabled: true },
  { label: "Data export", icon: Download, enabled: false },
]

const qrCells = [
  0, 1, 2, 4, 6, 7, 8, 10, 14, 16, 18, 19, 21, 24, 26, 28, 30, 31, 32, 34, 36, 38, 40, 42, 43, 44,
  46, 48,
]
const passportRecommendation = getCoachRecommendations("passport")[0]
const emptySummary: EqPassportSummary = {
  skillCount: allSkills.length,
  levelCount: allSkills.reduce((sum, skill) => sum + skill.levels.length, 0),
  verifiedLevelCount: 0,
  proofCount: 0,
  verifiedProofCount: 0,
  activeTaskCount: 0,
  progressPercent: 0,
}

export default function PassportScreen() {
  const auth = useAuth()
  const isSignedIn = Boolean(auth.session)
  const scrollY = useRef(new Animated.Value(0)).current
  const [contacts, setContacts] = useState<NetworkContact[]>(isSignedIn ? [] : mockContacts)
  const [passportSummary, setPassportSummary] = useState<EqPassportSummary>(emptySummary)
  const [draftName, setDraftName] = useState("")
  const [draftRole, setDraftRole] = useState("")
  const [contactSyncMessage, setContactSyncMessage] = useState(
    "Using local fallback contacts until Supabase contacts are available.",
  )
  const [contactSaving, setContactSaving] = useState(false)
  const [shareLoading, setShareLoading] = useState(false)
  const [shareMessage, setShareMessage] = useState("")
  const [publishedShareUrl, setPublishedShareUrl] = useState("")
  const [notificationMessage, setNotificationMessage] = useState("")
  const [notificationLoading, setNotificationLoading] = useState(false)
  const integrationStatuses = getIntegrationStatuses()
  const notificationPreferences = getNotificationPreferences(auth.profile)
  const publicPassport = getPublicPassportSettings({
    profile: auth.profile,
    email: auth.user?.email,
    userId: auth.user?.id,
  })
  const publicPassportPublished =
    publicPassport.enabled && publicPassport.visibilityMode !== "disabled"
  const sharePreviewUrl =
    publishedShareUrl ||
    (publicPassportPublished ? buildPublicPassportUrl(publicPassport.shareKey) : "")
  const displayName =
    auth.profile?.display_name || auth.user?.email?.split("@")[0] || "EMO.coach member"
  const summary = isSignedIn ? passportSummary : undefined
  const visibleStats = isSignedIn
    ? [
        { label: "Skills", value: `${summary?.skillCount ?? 0}` },
        { label: "Levels", value: `${summary?.verifiedLevelCount ?? 0}` },
        { label: "Proofs", value: `${summary?.proofCount ?? 0}` },
        { label: "Tasks", value: `${summary?.activeTaskCount ?? 0}` },
      ]
    : stats
  const progressPercent = isSignedIn ? (summary?.progressPercent ?? 0) : 35
  const visibleBadges = isSignedIn ? [] : passportBadges
  const visibleContacts = isSignedIn
    ? contacts.filter((contact) => isSupabaseUuid(contact.id))
    : contacts

  useEffect(() => {
    let cancelled = false

    async function loadContacts() {
      const result = await listEqContacts()
      if (cancelled) return

      if (result.mode === "supabase") {
        setContacts(result.items.length > 0 ? result.items : [])
        setContactSyncMessage(
          result.items.length > 0
            ? "Contacts loaded from Supabase for Coach Mode."
            : "Supabase contacts are ready. Add your first contact to replace demo data.",
        )
        return
      }

      setContacts(isSignedIn ? [] : mockContacts)
      setContactSyncMessage(result.message)
    }

    loadContacts()

    return () => {
      cancelled = true
    }
  }, [auth.session?.access_token, isSignedIn])

  useEffect(() => {
    let cancelled = false

    async function loadPassportSummary() {
      if (!isSignedIn) {
        setPassportSummary(emptySummary)
        return
      }

      const nextSummary = await getEqPassportSummary()
      if (!cancelled) setPassportSummary(nextSummary)
    }

    loadPassportSummary()

    return () => {
      cancelled = true
    }
  }, [auth.session?.access_token, isSignedIn])

  async function addContact() {
    const name = draftName.trim()
    if (!name || contactSaving) return

    setContactSaving(true)
    const result = await createEqContact({
      name,
      role: draftRole.trim() || "Personal contact",
      relationship: "Added by user",
      frequency: "Manual",
      context: "Available for Coach Mode suggestions from Supabase-backed My Network.",
    })
    const contactId = result.id ?? `c-manual-${Date.now()}`

    if (!isSignedIn || result.persisted) {
      setContacts((current) => [
        {
          id: contactId,
          name,
          role: draftRole.trim() || "Personal contact",
          frequency: "Manual",
          relationship: "Added by user",
          context:
            result.mode === "supabase"
              ? "Available for Coach Mode suggestions from Supabase-backed My Network."
              : "Available for Coach Mode suggestions in this local fallback session.",
          skillIds: ["active_listening_presence", "clear_communication_requests"],
        },
        ...current,
      ])
    }
    setContactSyncMessage(result.message)
    setDraftName("")
    setDraftRole("")
    setContactSaving(false)
  }

  async function publishPassport(options?: { open?: boolean }) {
    if (!auth.session) {
      router.push("/auth")
      return
    }

    setShareLoading(true)
    setShareMessage("")

    try {
      const result = await publishEqPassport({
        profile: auth.profile,
        email: auth.user?.email,
        userId: auth.user?.id,
      })

      await auth.refreshProfile()
      setPublishedShareUrl(result.url)
      setShareMessage("Public Passport link is ready.")

      if (options?.open) {
        router.push({
          pathname: "/p/[shareKey]",
          params: { shareKey: `@${result.settings.shareKey}` },
        })
        return
      }

      await sharePassportLink({
        title: "My EMO.coach Passport",
        message: `My EMO.coach Passport\n${result.url}`,
        url: result.url,
      })
    } catch (error) {
      setShareMessage(error instanceof Error ? error.message : String(error))
    } finally {
      setShareLoading(false)
    }
  }

  async function togglePushNotifications() {
    if (!auth.session) {
      router.push("/auth")
      return
    }

    setNotificationLoading(true)
    setNotificationMessage("")

    try {
      await updateNotificationPreferences({
        profile: auth.profile,
        preferences: {
          ...notificationPreferences,
          push: !notificationPreferences.push,
        },
      })
      await auth.refreshProfile()
      setNotificationMessage(
        !notificationPreferences.push
          ? "Push notifications enabled for this signed-in profile."
          : "Push notifications disabled for this signed-in profile.",
      )
    } catch (error) {
      setNotificationMessage(error instanceof Error ? error.message : String(error))
    } finally {
      setNotificationLoading(false)
    }
  }

  return (
    <SafeAreaView
      className="flex-1"
      edges={["top"]}
      style={{ backgroundColor: Colors.dark.surface }}
    >
      <CollapsibleScreenHeader
        scrollY={scrollY}
        eyebrow="Passport"
        title={screenCopy.passport.title}
        subtitle={screenCopy.passport.subtitle}
        statusText={auth.session ? "Synced" : "Local"}
        rightAction={
          <TouchableOpacity
            className="h-11 w-11 items-center justify-center rounded-full"
            disabled={shareLoading}
            style={{
              backgroundColor: `${Colors.dark.primary}24`,
              opacity: shareLoading ? 0.72 : 1,
            }}
            onPress={() => publishPassport()}
          >
            <Share2 color={Colors.dark.primary} size={19} />
          </TouchableOpacity>
        }
      />
      <Animated.ScrollView
        className="flex-1"
        contentContainerStyle={{
          padding: 24,
          paddingTop: COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
          paddingBottom: Platform.OS === "web" ? 112 : 96,
        }}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
          useNativeDriver: false,
        })}
      >
        <VStack className="items-center pb-7 pt-2">
          <Avatar size="2xl" className="border" style={{ borderColor: Colors.dark.primary }}>
            <AvatarFallbackText>{displayName}</AvatarFallbackText>
          </Avatar>
          <Text className="mt-4 text-3xl font-bold text-white">{displayName}</Text>
          <Text className="mt-1 font-bold" style={{ color: Colors.dark.primary }}>
            {isSignedIn
              ? `${passportSummary.verifiedLevelCount} verified levels`
              : "EQ Level 12 — Explorer"}
          </Text>
          <Text className="mt-2 text-center leading-5" style={{ color: Colors.dark.textSecondary }}>
            EMO.coach Passport for proven soft-skill and emotional-intelligence growth
          </Text>
        </VStack>

        <Box
          className="mb-5 rounded-3xl border p-5"
          style={{
            backgroundColor: auth.session ? `${Colors.dark.growth}17` : `${Colors.dark.primary}14`,
            borderColor: auth.session ? `${Colors.dark.growth}55` : `${Colors.dark.primary}44`,
          }}
        >
          <HStack className="items-start gap-3">
            <Box
              className="h-12 w-12 items-center justify-center rounded-2xl"
              style={{
                backgroundColor: auth.session
                  ? `${Colors.dark.growth}24`
                  : `${Colors.dark.primary}24`,
              }}
            >
              {auth.session ? (
                <ShieldCheck color={Colors.dark.growth} size={22} />
              ) : (
                <LogIn color={Colors.dark.primary} size={22} />
              )}
            </Box>
            <VStack className="flex-1 gap-2">
              <Text className="text-lg font-bold text-white">
                {auth.session ? "Supabase sync active" : "Sign in to sync progress"}
              </Text>
              <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
                {auth.session
                  ? `Signed in as ${auth.user?.email}. Proofs and tasks can persist to Supabase.`
                  : "Use Account to enable backend-backed proofs, tasks, and Passport progress."}
              </Text>
              <HStack className="gap-3">
                <Button
                  size="sm"
                  className="rounded-xl"
                  style={{
                    backgroundColor: auth.session ? Colors.dark.growth : Colors.dark.primary,
                  }}
                  onPress={() => router.push("/auth")}
                >
                  <ButtonText
                    className="font-bold"
                    style={{ color: Colors.dark.primaryForeground }}
                  >
                    Account
                  </ButtonText>
                </Button>
                {auth.session ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-xl border"
                    disabled={auth.actionLoading}
                    style={{ borderColor: `${Colors.dark.textSecondary}55` }}
                    onPress={auth.signOut}
                  >
                    <LogOut color={Colors.dark.textSecondary} size={14} />
                    <ButtonText className="font-bold" style={{ color: Colors.dark.textSecondary }}>
                      Sign Out
                    </ButtonText>
                  </Button>
                ) : null}
              </HStack>
            </VStack>
          </HStack>
        </Box>

        <Box
          className="mb-5 rounded-3xl border p-5"
          style={{
            backgroundColor: `${Colors.dark.growth}1A`,
            borderColor: `${Colors.dark.growth}47`,
          }}
        >
          <HStack className="items-center justify-between gap-4">
            <VStack className="flex-1">
              <Text className="text-xs font-bold" style={{ color: Colors.dark.growth }}>
                COACHING STREAK
              </Text>
              <Text className="text-3xl font-bold text-white">
                {isSignedIn ? `${passportSummary.activeTaskCount}` : "7 days"}
              </Text>
              <Text className="mt-1 text-sm" style={{ color: Colors.dark.textSecondary }}>
                {isSignedIn ? "Active Supabase-backed tasks." : "Keep coaching. Keep growing."}
              </Text>
            </VStack>
            <Box
              className="h-14 w-14 items-center justify-center rounded-full"
              style={{ backgroundColor: `${Colors.dark.growth}2E` }}
            >
              <TrendingUp color={Colors.dark.growth} size={24} />
            </Box>
          </HStack>
        </Box>

        <PassportCoachCard recommendation={passportRecommendation} />

        <Text className="mb-3 text-xl font-bold text-white">Intro</Text>
        <PressableScale onPress={() => router.push("/onboarding")}>
          <Box
            className="mb-5 rounded-3xl border p-4"
            style={{
              backgroundColor: Colors.dark.surfaceSecondary,
              borderColor: `${Colors.dark.primary}38`,
            }}
          >
            <HStack className="items-center gap-3">
              <Box
                className="h-12 w-12 items-center justify-center rounded-2xl"
                style={{ backgroundColor: `${Colors.dark.primary}24` }}
              >
                <PlayCircle color={Colors.dark.primary} size={22} />
              </Box>
              <VStack className="flex-1 gap-1">
                <Text className="text-xs font-bold" style={{ color: Colors.dark.primary }}>
                  INTRO
                </Text>
                <Text className="text-lg font-bold text-white">Open onboarding</Text>
                <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
                  Revisit the EMO.coach intro flow
                </Text>
              </VStack>
              <ChevronRight color={Colors.dark.textSecondary} size={20} />
            </HStack>
          </Box>
        </PressableScale>

        <Text className="mb-3 text-xl font-bold text-white">Growth Spaces</Text>
        <VStack className="mb-5 gap-3">
          <PassportLinkCard
            icon={BookOpenText}
            title="Development Journal"
            subtitle="Timeline of reflections, skill tags, and video proof placeholders"
            meta="Private by default"
            color={Colors.dark.proof}
            onPress={() => router.push("/journal")}
          />
          <PassportLinkCard
            icon={UsersRound}
            title="Growth Circle"
            subtitle="Supabase sharing when signed in, local fallback when unavailable"
            meta="Sharing is opt-in"
            color={Colors.dark.growth}
            onPress={() => router.push("/feed")}
          />
          <PassportLinkCard
            icon={MessageSquareWarning}
            title="Send Feedback"
            subtitle="Submit a bug, usability issue, or feature request"
            meta="Supabase queue"
            color={Colors.dark.proof}
            onPress={() => router.push("/modal")}
          />
        </VStack>

        <MyNetworkSection
          contacts={visibleContacts}
          draftName={draftName}
          draftRole={draftRole}
          syncMessage={contactSyncMessage}
          saving={contactSaving}
          onChangeName={setDraftName}
          onChangeRole={setDraftRole}
          onAddContact={addContact}
        />

        <Box
          className="mb-5 rounded-3xl border p-5"
          style={{
            backgroundColor: `${Colors.dark.growth}17`,
            borderColor: `${Colors.dark.growth}47`,
          }}
        >
          <HStack className="mb-4 items-center justify-between gap-3">
            <VStack className="flex-1">
              <Text className="text-xs font-bold" style={{ color: Colors.dark.growth }}>
                EQ LEVEL
              </Text>
              <Text className="text-2xl font-bold text-white">
                {isSignedIn ? `${progressPercent}/100 EQ` : "12 Explorer"}
              </Text>
            </VStack>
            <Box
              className="h-12 w-12 items-center justify-center rounded-full"
              style={{ backgroundColor: `${Colors.dark.growth}29` }}
            >
              <TrendingUp color={Colors.dark.growth} size={22} />
            </Box>
          </HStack>
          <Progress size="md" value={isSignedIn ? progressPercent : 68}>
            <ProgressFilledTrack style={{ backgroundColor: Colors.dark.growth }} />
          </Progress>
          <HStack className="mt-2 items-center justify-between">
            <Text className="text-xs" style={{ color: Colors.dark.textSecondary }}>
              {isSignedIn
                ? `${passportSummary.verifiedLevelCount} / ${passportSummary.levelCount} levels`
                : "680 / 1000 XP"}
            </Text>
            <Text className="text-xs font-bold" style={{ color: Colors.dark.growth }}>
              {isSignedIn
                ? `${passportSummary.verifiedProofCount} verified proofs`
                : "320 XP to Level 13"}
            </Text>
          </HStack>
        </Box>

        <HStack className="mb-5 gap-3">
          <Box
            className="flex-1 rounded-2xl border p-4"
            style={{
              backgroundColor: Colors.dark.surfaceSecondary,
              borderColor: `${Colors.dark.growth}38`,
            }}
          >
            <ProgressRing value={progressPercent} size={94} strokeWidth={9} label="EQ" />
          </Box>
          <VStack className="flex-[1.2] gap-3">
            <HStack className="gap-3">
              {visibleStats.slice(0, 2).map((stat) => (
                <StatCard key={stat.label} label={stat.label} value={stat.value} />
              ))}
            </HStack>
            <HStack className="gap-3">
              {visibleStats.slice(2).map((stat) => (
                <StatCard key={stat.label} label={stat.label} value={stat.value} />
              ))}
            </HStack>
          </VStack>
        </HStack>

        <Text className="mb-3 text-xl font-bold text-white">Achievement pathways</Text>
        <VStack className="mb-7 gap-3">
          {certificatePathways.map((pathway) => (
            <PathwayCard key={pathway.id} pathway={pathway} />
          ))}
        </VStack>

        <Text className="mb-3 text-xl font-bold text-white">My Badges</Text>
        {visibleBadges.length > 0 ? (
          <View className="mb-7 flex-row flex-wrap justify-between gap-y-3">
            {visibleBadges.map((badge, index) => (
              <PressableScale key={badge.id} style={{ width: "48%" }}>
                <Box
                  className="h-36 overflow-hidden rounded-2xl border p-4"
                  style={{
                    backgroundColor:
                      index % 2 === 0 ? `${badge.color}12` : Colors.dark.surfaceSecondary,
                    borderColor: `${badge.color}55`,
                    shadowColor: badge.color,
                    shadowOpacity: 0.12,
                    shadowRadius: 10,
                    shadowOffset: { width: 0, height: 6 },
                  }}
                >
                  <HStack className="mb-3 min-h-10 items-center gap-2">
                    <Trophy color={badge.color} size={20} />
                    <Text className="flex-1 font-bold text-white" numberOfLines={2}>
                      {badge.title}
                    </Text>
                  </HStack>
                  <Text
                    className="text-xs leading-4"
                    style={{ color: Colors.dark.textSecondary }}
                    numberOfLines={2}
                  >
                    {badge.subtitle}
                  </Text>
                  <Text className="mt-4 text-xs font-bold" style={{ color: badge.color }}>
                    BADGE PREVIEW
                  </Text>
                </Box>
              </PressableScale>
            ))}
          </View>
        ) : (
          <Box className="mb-7">
            <EmptyState
              icon={ShieldCheck}
              title="No badges yet"
              description="Coach levels and log proof to build your portable EMO.coach Passport."
            />
          </Box>
        )}

        <Box
          className="mb-4 overflow-hidden rounded-3xl border p-5"
          style={{
            backgroundColor: `${Colors.dark.proof}1A`,
            borderColor: `${Colors.dark.proof}61`,
            shadowColor: Colors.dark.proof,
            shadowOpacity: 0.18,
            shadowRadius: 18,
            shadowOffset: { width: 0, height: 10 },
          }}
        >
          <View
            className="absolute -right-10 -top-12 h-32 w-32 rounded-full"
            style={{ backgroundColor: `${Colors.dark.proof}24` }}
          />
          <HStack className="items-center gap-4">
            <Box
              className="h-24 w-24 flex-row flex-wrap rounded-2xl border p-2"
              style={{
                backgroundColor: Colors.dark.surfaceElevated,
                borderColor: "rgba(255,255,255,0.1)",
              }}
            >
              {Array.from({ length: 49 }).map((_, index) => (
                <View
                  key={index}
                  style={{
                    width: "14.28%",
                    height: "14.28%",
                    backgroundColor: qrCells.includes(index) ? Colors.dark.proof : "transparent",
                  }}
                />
              ))}
            </Box>
            <VStack className="flex-1 gap-2">
              <HStack className="items-center gap-2">
                <QrCode color={Colors.dark.proof} size={18} />
                <Text className="font-bold text-white">Passport share preview</Text>
              </HStack>
              <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
                {publicPassportPublished
                  ? `Live Passport link: ${sharePreviewUrl}`
                  : `${screenCopy.passport.shareAudience} Sign in and publish to create a private-link public Passport.`}
              </Text>
              {shareMessage ? (
                <Text className="text-xs font-bold" style={{ color: Colors.dark.proof }}>
                  {shareMessage}
                </Text>
              ) : null}
            </VStack>
          </HStack>
        </Box>

        <HStack className="mb-7 gap-3">
          <Button
            size="xl"
            className="flex-1 rounded-2xl"
            disabled={shareLoading}
            style={{
              backgroundColor: Colors.dark.primary,
              opacity: shareLoading ? 0.72 : 1,
              shadowColor: Colors.dark.primary,
              shadowOpacity: 0.28,
              shadowRadius: 14,
              shadowOffset: { width: 0, height: 8 },
            }}
            onPress={() => publishPassport()}
          >
            <Share2 color={Colors.dark.primaryForeground} size={18} />
            <ButtonText className="font-bold" style={{ color: Colors.dark.primaryForeground }}>
              {shareLoading ? "Publishing" : "Share Passport"}
            </ButtonText>
          </Button>
          <Button
            size="xl"
            variant="outline"
            className="rounded-2xl border"
            disabled={shareLoading}
            style={{ borderColor: `${Colors.dark.primary}6B`, opacity: shareLoading ? 0.72 : 1 }}
            onPress={() => publishPassport({ open: true })}
          >
            <Download color={Colors.dark.primary} size={18} />
          </Button>
        </HStack>

        <Text className="mb-3 text-xl font-bold text-white">Settings</Text>
        <VStack className="gap-3">
          <TouchableOpacity
            activeOpacity={0.85}
            disabled={notificationLoading}
            onPress={togglePushNotifications}
          >
            <Box
              className="rounded-2xl border p-4"
              style={{
                backgroundColor: Colors.dark.surfaceSecondary,
                borderColor: notificationPreferences.push
                  ? `${Colors.dark.growth}55`
                  : "rgba(255,255,255,0.06)",
                opacity: notificationLoading ? 0.72 : 1,
              }}
            >
              <HStack className="items-center justify-between gap-4">
                <HStack className="flex-1 items-center gap-3">
                  <Bell color={Colors.dark.primary} size={20} />
                  <VStack className="flex-1 gap-1">
                    <Text className="font-bold text-white">Notifications</Text>
                    {notificationMessage ? (
                      <Text className="text-xs leading-4" style={{ color: Colors.dark.proof }}>
                        {notificationMessage}
                      </Text>
                    ) : null}
                  </VStack>
                </HStack>
                <Switch
                  value={notificationPreferences.push}
                  disabled={notificationLoading}
                  trackColor={{
                    false: Colors.dark.surfaceTertiary,
                    true: `${Colors.dark.primary}73`,
                  }}
                  thumbColor={
                    notificationPreferences.push ? Colors.dark.primary : Colors.dark.textSecondary
                  }
                />
              </HStack>
            </Box>
          </TouchableOpacity>
          {settings.map((setting) => {
            const Icon = setting.icon
            return (
              <TouchableOpacity key={setting.label} activeOpacity={0.85}>
                <Box
                  className="rounded-2xl border p-4"
                  style={{
                    backgroundColor: Colors.dark.surfaceSecondary,
                    borderColor: "rgba(255,255,255,0.06)",
                  }}
                >
                  <HStack className="items-center justify-between">
                    <HStack className="items-center gap-3">
                      <Icon color={Colors.dark.primary} size={20} />
                      <Text className="font-bold text-white">{setting.label}</Text>
                    </HStack>
                    <Switch
                      value={setting.enabled}
                      trackColor={{
                        false: Colors.dark.surfaceTertiary,
                        true: `${Colors.dark.primary}73`,
                      }}
                      thumbColor={setting.enabled ? Colors.dark.primary : Colors.dark.textSecondary}
                    />
                  </HStack>
                </Box>
              </TouchableOpacity>
            )
          })}
        </VStack>

        <Text className="mb-3 mt-7 text-xl font-bold text-white">Integrations</Text>
        <VStack className="gap-3">
          {integrationStatuses.map((integration) => {
            const connected =
              integration.status === "configured" || integration.status === "connected"
            const color = connected ? Colors.dark.growth : Colors.dark.textSecondary

            return (
              <Box
                key={integration.id}
                className="rounded-2xl border p-4"
                style={{
                  backgroundColor: Colors.dark.surfaceSecondary,
                  borderColor: connected ? `${Colors.dark.growth}55` : "rgba(255,255,255,0.06)",
                }}
              >
                <HStack className="items-start gap-3">
                  <Box
                    className="h-10 w-10 items-center justify-center rounded-full"
                    style={{ backgroundColor: connected ? `${Colors.dark.growth}24` : "#FFFFFF10" }}
                  >
                    <ShieldCheck color={color} size={18} />
                  </Box>
                  <VStack className="flex-1 gap-1">
                    <HStack className="items-center justify-between gap-3">
                      <Text className="font-bold text-white">{integration.label}</Text>
                      <Text className="text-xs font-bold uppercase" style={{ color }}>
                        {integration.status}
                      </Text>
                    </HStack>
                    <Text
                      className="text-sm leading-5"
                      style={{ color: Colors.dark.textSecondary }}
                    >
                      {integration.message}
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            )
          })}
        </VStack>
      </Animated.ScrollView>
    </SafeAreaView>
  )
}

function PassportCoachCard({
  recommendation,
}: {
  recommendation: CoachRecommendation | undefined
}) {
  if (!recommendation) return null

  return (
    <PressableScale onPress={() => router.push(recommendation.route as never)}>
      <Box
        className="mb-5 rounded-3xl border p-5"
        style={{
          backgroundColor: `${recommendation.accentColor}14`,
          borderColor: `${recommendation.accentColor}44`,
        }}
      >
        <HStack className="items-start gap-3">
          <Box
            className="h-11 w-11 items-center justify-center rounded-2xl"
            style={{ backgroundColor: `${recommendation.accentColor}22` }}
          >
            <Sparkles color={recommendation.accentColor} size={19} />
          </Box>
          <VStack className="flex-1 gap-1">
            <Text className="text-xs font-bold" style={{ color: recommendation.accentColor }}>
              PASSPORT NEXT STEP
            </Text>
            <Text className="text-lg font-bold text-white">{recommendation.title}</Text>
            <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
              {recommendation.reason}
            </Text>
          </VStack>
          <ChevronRight color={recommendation.accentColor} size={20} />
        </HStack>
      </Box>
    </PressableScale>
  )
}

function MyNetworkSection({
  contacts,
  draftName,
  draftRole,
  syncMessage,
  saving,
  onChangeName,
  onChangeRole,
  onAddContact,
}: {
  contacts: NetworkContact[]
  draftName: string
  draftRole: string
  syncMessage: string
  saving: boolean
  onChangeName: (value: string) => void
  onChangeRole: (value: string) => void
  onAddContact: () => void | Promise<void>
}) {
  return (
    <Box
      className="mb-6 rounded-3xl border p-5"
      style={{
        backgroundColor: Colors.dark.surfaceSecondary,
        borderColor: `${Colors.dark.primary}3D`,
      }}
    >
      <HStack className="mb-4 items-start gap-3">
        <Box
          className="h-11 w-11 items-center justify-center rounded-2xl"
          style={{ backgroundColor: `${Colors.dark.primary}26` }}
        >
          <UsersRound color={Colors.dark.primary} size={20} />
        </Box>
        <VStack className="flex-1 gap-1">
          <Text className="text-xs font-bold" style={{ color: Colors.dark.primary }}>
            MY NETWORK
          </Text>
          <Text className="text-xl font-bold text-white">Contacts for Coach Mode</Text>
          <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
            Supabase contacts power Coach Mode when signed in; local contacts remain available as a
            fallback.
          </Text>
          <Text className="text-xs font-bold" style={{ color: Colors.dark.primary }}>
            {syncMessage}
          </Text>
        </VStack>
      </HStack>

      <VStack className="mb-5 gap-3">
        <TextInput
          value={draftName}
          onChangeText={onChangeName}
          placeholder="Contact name"
          placeholderTextColor={Colors.dark.textSecondary}
          style={{
            minHeight: 46,
            borderRadius: 16,
            borderWidth: 1,
            borderColor: "rgba(255,255,255,0.08)",
            backgroundColor: Colors.dark.surface,
            color: "white",
            paddingHorizontal: 14,
            paddingVertical: 10,
            fontWeight: "700",
          }}
        />
        <HStack className="gap-2">
          <TextInput
            value={draftRole}
            onChangeText={onChangeRole}
            placeholder="Role or context"
            placeholderTextColor={Colors.dark.textSecondary}
            style={{
              flex: 1,
              minHeight: 46,
              borderRadius: 16,
              borderWidth: 1,
              borderColor: "rgba(255,255,255,0.08)",
              backgroundColor: Colors.dark.surface,
              color: "white",
              paddingHorizontal: 14,
              paddingVertical: 10,
              fontWeight: "700",
            }}
          />
          <TouchableOpacity activeOpacity={0.86} disabled={saving} onPress={onAddContact}>
            <Box
              className="h-[46px] w-[52px] items-center justify-center rounded-2xl"
              style={{ backgroundColor: Colors.dark.primary, opacity: saving ? 0.72 : 1 }}
            >
              <UserPlus color={Colors.dark.primaryForeground} size={20} />
            </Box>
          </TouchableOpacity>
        </HStack>
      </VStack>

      {contacts.length > 0 ? (
        <VStack className="gap-3">
          {contacts.map((contact) => (
            <PressableScale
              key={contact.id}
              onPress={() =>
                router.push({
                  pathname: "/contact/[id]",
                  params: {
                    id: contact.id,
                    name: contact.name,
                    role: contact.role,
                    relationship: contact.relationship,
                    context: contact.context,
                  },
                })
              }
            >
              <Box
                className="rounded-2xl border p-4"
                style={{
                  backgroundColor: Colors.dark.surface,
                  borderColor: "rgba(255,255,255,0.07)",
                }}
              >
                <HStack className="items-start gap-3">
                  <Box
                    className="h-10 w-10 items-center justify-center rounded-full"
                    style={{ backgroundColor: `${Colors.dark.primary}24` }}
                  >
                    <Text className="font-bold" style={{ color: Colors.dark.primary }}>
                      {contact.name
                        .split(" ")
                        .map((part) => part[0])
                        .join("")
                        .slice(0, 2)}
                    </Text>
                  </Box>
                  <VStack className="flex-1 gap-1">
                    <Text className="font-bold text-white">{contact.name}</Text>
                    <Text className="text-sm" style={{ color: Colors.dark.textSecondary }}>
                      {contact.role} · {contact.frequency}
                    </Text>
                    <Text className="text-xs leading-4" style={{ color: Colors.dark.textTertiary }}>
                      {contact.context}
                    </Text>
                  </VStack>
                  <ChevronRight color={Colors.dark.textSecondary} size={18} />
                </HStack>
              </Box>
            </PressableScale>
          ))}
        </VStack>
      ) : (
        <EmptyState
          icon={UsersRound}
          title="No saved contacts yet"
          description="Add a person to make Coach Mode suggestions relationship-aware."
        />
      )}
    </Box>
  )
}

function PathwayCard({ pathway }: { pathway: CertificatePathway }) {
  const skill = getSkill(pathway.skillId)
  const color = getSkillDomainColor(pathway.skillId)
  const progress = (pathway.currentLevel / pathway.targetLevel) * 100

  return (
    <PressableScale onPress={() => router.push(`/skill/${pathway.skillId}`)}>
      <Box
        className="rounded-3xl border p-4"
        style={{
          backgroundColor: Colors.dark.surfaceSecondary,
          borderColor: `${color}44`,
        }}
      >
        <HStack className="mb-3 items-start gap-3">
          <Box
            className="h-11 w-11 items-center justify-center rounded-2xl"
            style={{ backgroundColor: `${color}22` }}
          >
            <Award color={color} size={20} />
          </Box>
          <VStack className="flex-1 gap-1">
            <Text className="text-xs font-bold" style={{ color }}>
              {skill.title}
            </Text>
            <Text className="text-lg font-bold text-white">{pathway.title}</Text>
            <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
              To reach Level {pathway.targetLevel}: {pathway.proofsRemaining} proofs +{" "}
              {pathway.coursesRemaining} course. {pathway.streakLabel}.
            </Text>
          </VStack>
          <ChevronRight color={color} size={19} />
        </HStack>
        <Progress size="sm" value={progress}>
          <ProgressFilledTrack style={{ backgroundColor: color }} />
        </Progress>
        <HStack className="mt-3 items-center justify-between gap-3">
          <Text className="text-xs" style={{ color: Colors.dark.textSecondary }}>
            Level {pathway.currentLevel}/{pathway.targetLevel}
          </Text>
          <Text className="flex-1 text-right text-xs font-bold" style={{ color }}>
            Earns: {pathway.certificateTitle}
          </Text>
        </HStack>
      </Box>
    </PressableScale>
  )
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Box
      className="flex-1 rounded-2xl border p-3"
      style={{
        backgroundColor: Colors.dark.surfaceSecondary,
        borderColor: "rgba(255,255,255,0.06)",
      }}
    >
      <Text className="text-2xl font-bold text-white">{value}</Text>
      <Text className="text-xs" style={{ color: Colors.dark.textSecondary }}>
        {label}
      </Text>
    </Box>
  )
}

function PassportLinkCard({
  icon: Icon,
  title,
  subtitle,
  meta,
  color,
  onPress,
}: {
  icon: typeof BookOpenText
  title: string
  subtitle: string
  meta: string
  color: string
  onPress: () => void
}) {
  return (
    <PressableScale onPress={onPress}>
      <Box
        className="rounded-3xl border p-4"
        style={{
          backgroundColor: Colors.dark.surfaceSecondary,
          borderColor: `${color}44`,
        }}
      >
        <HStack className="items-center gap-3">
          <Box
            className="h-12 w-12 items-center justify-center rounded-2xl"
            style={{ backgroundColor: `${color}22` }}
          >
            <Icon color={color} size={21} />
          </Box>
          <VStack className="flex-1 gap-1">
            <Text className="text-xs font-bold" style={{ color }}>
              {meta}
            </Text>
            <Text className="text-lg font-bold text-white">{title}</Text>
            <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
              {subtitle}
            </Text>
          </VStack>
          <ChevronRight color={color} size={20} />
        </HStack>
      </Box>
    </PressableScale>
  )
}
